import { useMemo } from "react";
import type { DBProject, DBInvoice } from "@/lib/db-types";
import { effectiveInvoiceTerms, pctToAmount, planInvoices, type InvoiceTerm } from "@/lib/invoice-plan";

export interface UpsellSummary {
  id: string;
  description: string;
  amount_excl_vat: number;
  added_to_invoice_id: string | null;
  /** VIB-994: 'tm'-mersalg fakturers løpende (invoices.upsell_id, ikke added_to_invoice_id)
   *  og har alltid amount_excl_vat=0 — holdes derfor ute av "ufakturert mersalg"-lista
   *  (den listen/knappen antar én fast sum + én-til-én-faktura-kobling). Valgfri felt:
   *  fravær tolkes som 'fixed' (bakoverkompatibelt med eksisterende kallsteder/tester).
   *  For fastpris: amount_excl_vat er kundepris (linjer + overhead − rabatt), ikke rå linjesum. */
  billing_mode?: "fixed" | "tm";
}

export type MilestoneTerm = InvoiceTerm;

export interface InvoiceCalculations {
  contractAmount: number;
  /** Kontraktssum eks. mersalg (grunntilbud) = total_amount − alt mersalg. */
  contractExclUpsells: number;
  invoicedExclVat: number;
  /** Gjenstående grunntilbud, eks. ufakturert mersalg (rest kontrakt + ufakt. mersalg = totalRemaining). */
  remainingContract: number;
  uninvoicedUpsells: UpsellSummary[];
  uninvoicedUpsellTotal: number;
  totalRemaining: number;
  hasStartupInvoice: boolean;
  startupPct: number;
  startupAmount: number;
  /** Gjeldende faktureringsplan (oppstart fra startup_invoice_pct, sluttleddet tar differansen). */
  planTerms: InvoiceTerm[];
  /** Milepæler mellom oppstart og slutt — tom hvis prosjektet bare har 2 terminer. */
  milestoneTerms: MilestoneTerm[];
  /** Neste ikke-opprettede milepæl, eller null hvis ingen flere. */
  nextMilestone: MilestoneTerm | null;
  nextMilestoneIndex: number;
  nextMilestoneAmount: number;
}

/**
 * Pure beregning av faktureringstotaler for et prosjekt. Eksportert separat så
 * vi kan unit-teste uten å mounte React.
 */
export function computeInvoiceCalculations(
  project: DBProject,
  invoices: DBInvoice[],
  upsells: UpsellSummary[],
): InvoiceCalculations {
  const contractAmount = Number(project.total_amount ?? 0);
  // VIB-994 (fix, review 21.07): `invoices.upsell_id` er KUN satt for T&M-
  // fakturaer (fastpris-slutt bruker `upsells.added_to_invoice_id`, ikke dette
  // feltet) — så det skiller T&M-fakturaer rent fra kontrakt-fakturaer.
  // Kontraktssummen (project.total_amount) inkluderer ALDRI T&M-mersalg
  // (amount_excl_vat holdes 0, håndhevet i recomputeUpsellAmount) — å telle
  // T&M-fakturaer inn i invoicedExclVat ville nettet T&M-inntekten bort fra
  // "gjenstående til fakturering" og fått sluttfakturaen til å bli for lav
  // (dobbelttellings-bug: reell tapt inntekt i prod). Se regresjonstesten
  // "VIB-994: T&M-faktura netter IKKE kontrakt-remaining" i
  // __tests__/use-invoice-calculations.test.ts.
  const contractInvoices = invoices.filter((i) => !i.upsell_id);
  const invoicedExclVat = contractInvoices.reduce(
    (s, i) => s + Number(i.amount_excl_vat ?? i.amount ?? 0),
    0,
  );

  // VIB-994: T&M-mersalg ekskluderes — de fakturers løpende via egen T&M-seksjon
  // (invoices.upsell_id), ikke som en fast sum i "ufakturert mersalg". Deres
  // amount_excl_vat er uansett alltid 0 (håndhevet i recomputeUpsellAmount), så
  // dette er et rent visnings-/dobbelttellings-vern, ikke en beløpsendring.
  // Fastpris: amount_excl_vat er kundepris (linjer + overhead − rabatt), samme som
  // Mersalg-fanen viser — ikke rå sum av budget_lines.amount.
  const uninvoicedUpsells = upsells.filter(
    (u) => !u.added_to_invoice_id && u.billing_mode !== "tm",
  );
  const uninvoicedUpsellTotal = uninvoicedUpsells.reduce(
    (s, u) => s + u.amount_excl_vat,
    0,
  );

  // VIB-1120: compute_project_total folder ALT aktivt (ikke-slettet) mersalg inn
  // i project.total_amount. «Grunntilbudet» (kontrakt eks. mersalg) er derfor
  // total_amount minus alt mersalget (kundepris per mersalg, ikke rå linjesum).
  const upsellTotalAll = upsells.reduce((s, u) => s + u.amount_excl_vat, 0);
  const contractExclUpsells = contractAmount - upsellTotalAll;

  // Totalt gjenstående = hele kontrakten (inkl. mersalg) − fakturert.
  const totalRemaining = contractAmount - invoicedExclVat;
  // «Rest kontrakt» = gjenstående grunntilbud, eks. ufakturert mersalg, slik at
  // rest kontrakt + ufakturert mersalg = totalt gjenstående. (Mersalget ligger
  // allerede i total_amount, så vi trekker det ut her og viser det som egen post
  // i stedet for å legge det dobbelt på toppen.)
  const remainingContract = totalRemaining - uninvoicedUpsellTotal;

  // Kreditnotaer og fullt krediterte fakturaer teller ikke i planen: en
  // kreditert oppstartsfaktura (f.eks. fordi kontraktssummen var feil)
  // nullstiller leddet, så oppstartsfakturaen kan sendes på nytt. Summen
  // over (invoicedExclVat) netter uansett, fordi kreditnotaen er negativ.
  const activeInvoices = planInvoices(invoices);
  const hasStartupInvoice = activeInvoices.some((i) => i.invoice_type === "startup");
  const startupPct = project.startup_invoice_pct ?? 60;
  const startupAmount = (contractAmount * startupPct) / 100;

  // VIB-686/2836: mellomfakturaer (terminene mellom oppstart og slutt i
  // invoice_terms). Første termin er "oppstart" (invoice_type='startup'), siste
  // er "slutt" (invoice_type='final', håndtert i Gjenstående-kortet).
  // Mellomfakturaer lagres som invoice_type='interim' med milestone_index
  // (0-basert posisjon blant mellomleddene).
  const planTerms = effectiveInvoiceTerms(project);
  const milestoneTerms = planTerms.slice(1, -1);
  // BUG-M2: tell faktiske mellomfakturaer via milestone_index (eldre
  // terminfakturaer er invoice_type='upsell', som også dekker ekte mersalg).
  // Ad hoc-mellomfakturaer legges inn i planen og har også milestone_index.
  const existingMilestoneCount = activeInvoices.filter(
    (i) => i.milestone_index != null,
  ).length;
  const nextMilestoneIndex = existingMilestoneCount;
  const nextMilestone = milestoneTerms[nextMilestoneIndex] ?? null;
  // VIB-2836: Gjenstående er fasit. Et planlagt ledd foreslår aldri mer enn det
  // som gjenstår av kontrakten (kappes ned); sluttfakturaen tar resten.
  const nextMilestoneAmount = nextMilestone
    ? Math.min(pctToAmount(nextMilestone.pct, contractAmount), Math.max(0, remainingContract))
    : 0;

  return {
    contractAmount,
    contractExclUpsells,
    invoicedExclVat,
    remainingContract,
    uninvoicedUpsells,
    uninvoicedUpsellTotal,
    totalRemaining,
    hasStartupInvoice,
    startupPct,
    startupAmount,
    planTerms,
    milestoneTerms,
    nextMilestone,
    nextMilestoneIndex,
    nextMilestoneAmount,
  };
}

/**
 * useMemo-wrapper for bruk i komponenter.
 */
export function useInvoiceCalculations(
  project: DBProject,
  invoices: DBInvoice[],
  upsells: UpsellSummary[],
): InvoiceCalculations {
  return useMemo(
    () => computeInvoiceCalculations(project, invoices, upsells),
    [project, invoices, upsells],
  );
}
